import { useEffect, useState } from 'react'
import { FaChevronLeft } from 'react-icons/fa'
import { Link, useLocation, useNavigate, useParams, Outlet } from 'react-router-dom'
import { useEpisode } from '@/contexts/EpisodeContext'
import Button from '@/components/ui/Button'
import { FiDownload, FiMoreHorizontal, FiTrash2, FiPlus, FiChevronUp, FiChevronDown, FiChevronsLeft, FiChevronsRight, FiCopy, FiMessageSquare } from 'react-icons/fi'
import { LuLetterText, LuBrainCircuit } from "react-icons/lu"
import { RiPlayList2Fill } from "react-icons/ri"
import { CiViewTimeline } from "react-icons/ci"
import { RiFilmLine } from "react-icons/ri"
import { useShow } from '@/contexts/ShowContext'
import { apiFetch } from '@/lib/api'
import { Button as ShadButton } from '@/components/ui/shadcn/button'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import useFileDownload from '@/hooks/useFileDownload'
import useStoryboardExport from '@/hooks/useStoryboardExport'
import { TbFileTypeZip, TbFileText } from "react-icons/tb";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger} from "@/components/ui/shadcn/dropdown-menu"
import Loader from '@/components/loader'
import { LuLoaderCircle, LuCheck } from 'react-icons/lu'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
dayjs.extend(duration)
import ConfirmModal from '@/components/modals/ConfirmModal'
import { useModal } from '@/contexts/ModalContext'
import { UserRole } from '@/contexts/UserContext'
import { apiCreateTimeline } from '@/components/TimelineEditor/api'
import { DEFAULT_TRACKS } from '@/components/TimelineEditor/store'

export default function EpisodeLayout() {
    const [loading, setLoading] = useState(true)
    const [updating, setUpdating] = useState(false)
    const [loadingStatus, setLoadingStatus] = useState('')
    const [error, setError] = useState(null)
    const {episodeId, segmentId} = useParams()
    const {show} = useShow()
    const location = useLocation()
    const pathname = location.pathname
    const { openModal } = useModal()
    const {updateEpisode, episode, episodeSaving} = useEpisode()
    const { runTaskAndPoll, taskStatus } = useKagamiBridge()
    const { downloadFile } = useFileDownload()
    const { exportStoryboard, exporting } = useStoryboardExport()
    const [mode, setMode] = useState(pathname.split('/').pop() || 'script')
    const [segmentsPanelCollapsed, setSegmentsPanelCollapsed] = useState(false)
    const navigate = useNavigate()

    useEffect(()=>{
        setMode(pathname.split('/').pop() || 'script')
    },[pathname])
    
    const getEpisode = async () => {
        try {
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}`)
            if (!response.ok) {
                throw new Error('Failed to fetch show')
            }
            const data = await response.json()
            updateEpisode(data)
            return data
        } catch (error) {
            setError(error.message)
        }
    }

    const handleExportStoryboardAssets = async () => {
        if (!show?._id || !episode?._id || !segmentId) return
        try {
            const res = await apiFetch(`/api/shows/${show._id}/episodes/${episode._id}/segments/${segmentId}/storyboard`)
            if (!res.ok) return
            const storyboard = await res.json()
            const segment = episode?.segments?.find(s => s._id === segmentId)
            const name = `${episode?.name || 'Episode'} - ${segment?.name || 'Segment'} - Storyboard`
            exportStoryboard(storyboard, segment, name)
        } catch (err) {
            console.error('Error exporting storyboard assets:', err)
        }
    }

    const handleExport = async (format='zip') =>{
        const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/export/${format}`)
        if (!response.ok) {
            throw new Error('Failed to export episode')
        }
        const blob = await response.blob()

        const contentDisposition = response.headers.get('Content-Disposition')
        let filename = 'scene.zip'
        
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '')
            }
        }
        
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        
        // Trigger download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clean up
        URL.revokeObjectURL(url)
    }

    const handleDeleteSegment = async (segment) => {
        const confirmed = await openModal(<ConfirmModal title='Delete Segment' message='Are you sure you want to delete this segment?' onClose={() => setDeleting(false)} />)
        if(!confirmed) return

        const deleteIndex = episode.segments.findIndex(s => s._id === segment._id)

        try{
            setUpdating(true)
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments/${segment._id}`, {
                method: 'DELETE'
            })

            if(!response.ok){
                throw new Error('Failed to delete segment')
            }
            // const deletedSegment = await response.json()
            const newEpisode = await getEpisode()
            if(newEpisode.segments.length){
                if(deleteIndex < newEpisode.segments.length){
                    navigate(`/show/episodes/${episodeId}/${newEpisode.segments[deleteIndex]._id}/${mode}`)
                }else{
                    navigate(`/show/episodes/${episodeId}/${newEpisode.segments[deleteIndex-1]._id}/${mode}`)
                }
            }else{
                navigate(`/show/episodes/${episodeId}`)
            }
        }catch(error){
            console.error('Error deleting segment:', error)
        }finally{
            setUpdating(false)
        }
    }

    const handleAddSegment = async () => {
        try{
            setUpdating(true)
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}/segments`, {
                method: 'POST',
                body: JSON.stringify({name: 'New Segment'})
            })
            if(!response.ok){
                throw new Error('Failed to create segment')
            }

            const prevSegmentsCount = episode.segments.length
            const newSegment = await response.json()
            await getEpisode()

            if(prevSegmentsCount > 0){
                navigate(`/show/episodes/${episodeId}/${newSegment._id}/script`)
            }
        }
        catch(error){
            console.error('Error creating segment:', error)
        }finally{
            setUpdating(false)
        }
    }

    const createConversation = async (segment) => {
        const res = await apiFetch(`/api/shows/${show._id}/conversations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: segment.name || 'New Chat',
                episode: episodeId,
                segment: segment._id,
            }),
        })
        if (!res.ok) throw new Error('Failed to create conversation')
        navigate('/show/agent')
    }

    const handleNewChat = async (segment) => {
        try {
            const res = await apiFetch(`/api/shows/${show._id}/conversations?segment=${segment._id}`)
            const existing = res.ok ? await res.json() : []

            if (existing.length > 0) {
                const count = existing.length
                const latestConv = existing[0]
                await openModal(
                    <ConfirmModal
                        type='warning'
                        title='Existing Conversation'
                        message={`There ${count === 1 ? 'is' : 'are'} already ${count} conversation${count === 1 ? '' : 's'} for this segment. Would you like to go to the most recent conversation or create a new one?`}
                        buttons={[
                            {
                                label: 'Go to Recent',
                                type: 'secondary',
                                onClick: () => navigate(`/show/agent?conversationId=${latestConv._id}`),
                            },
                            {
                                label: 'Create New',
                                type: 'primary',
                                onClick: () => createConversation(segment),
                            },
                        ]}
                    />
                )
            } else {
                await createConversation(segment)
            }
        } catch (err) {
            console.error('Error creating chat:', err)
        }
    }

    const handleReorderSegment = async (segment, index) => {
        try{
            const segments = episode.segments.filter(s => s._id !== segment._id)
            segments.splice(index, 0, segment)

            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}`, {
                method: 'PUT',
                body: JSON.stringify({segments})
            })
            
            if(!response.ok){
                throw new Error('Failed to reorder segment')
            }
            await getEpisode()
        }catch(error){
            console.error('Error reordering segment:', error)
        }
    }

    const createTimeline = async (segment) => {
        setUpdating(true)
        try {
            const timeline = await apiCreateTimeline(show._id, {
                name: `${episode.name} - ${segment.name}`,
                tracks: DEFAULT_TRACKS,
                clips: [],
                assets: {},
                episode: episodeId,
                segment: segment._id,
            })
            navigate(`/show/timelines/${timeline._id}`)
        } catch (error) {
            console.error('Error creating timeline:', error)
        } finally {
            setUpdating(false)
        }
    }

    const handleCreateTimeline = async (segment) => {
        try {
            const res = await apiFetch(`/api/shows/${show._id}/timelines?segment=${segment._id}&sortBy=updatedAt&sortDir=desc`)
            const result = res.ok ? await res.json() : { data: [] }
            const timelines = result.data || []

            if (timelines.length > 0) {
                const count = timelines.length
                const latest = timelines[0]
                await openModal(
                    <ConfirmModal
                        type='warning'
                        title='Existing Timeline'
                        message={`There ${count === 1 ? 'is' : 'are'} already ${count} timeline${count === 1 ? '' : 's'} for this segment. Would you like to go to the most recent timeline or create a new one?`}
                        buttons={[
                            {
                                label: 'Go to Recent',
                                type: 'secondary',
                                onClick: () => navigate(`/show/timelines/${latest._id}`),
                            },
                            {
                                label: 'Create New',
                                type: 'primary',
                                onClick: () => createTimeline(segment),
                            },
                        ]}
                    />
                )
            } else {
                await createTimeline(segment)
            }
        } catch (err) {
            console.error('Error creating timeline:', err)
        }
    }

    useEffect(()=>{
        setLoadingStatus(taskStatus)
    },[taskStatus])

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            await getEpisode()
            setLoading(false)
        }
        load()
    }, [episodeId])

    const isSegmentActive = (path) => {
        return pathname === path ? 'text-primary pointer-events-none' : 'text-secondary-foreground/80 hover:text-secondary-foreground'
    }

    const isModeActive = (m) => {
        return pathname.endsWith(m) ? 'text-black bg-primary pointer-events-none' : 'bg-secondary/60 text-secondary-foreground/80 hover:bg-secondary hover:text-secondary-foreground'
    }

    if (loading) {
        return <Loader />
    }

    if (error) {
        return (
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                <span className="text-red-400">Error: {error}</span>
            </div>
        )
    }

    return (
        <div className='flex flex-col h-screen overflow-hidden'>
            
                <div className='flex flex-col gap-4 w-full py-6 px-6 border-b border-b-secondary/60'>
                    <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                            <Link to={`/show/episodes`} className='text-secondary-foreground hover:text-white cursor-pointer px-2 flex justify-center items-center rounded-md'>
                                <FaChevronLeft className='w-4 h-4' />
                            </Link>                              
                            <h1 className="text-3xl font-bold text-secondary-foreground">{episode.name}</h1>
                            {/* <div className='flex items-center gap-2 w-20 text-secondary-foreground/60'>
                                {episodeSaving ? <LuLoaderCircle className='w-4 h-4 animate-spin' /> : <LuCheck className='w-4 h-4' />}
                                <span className='text-sm'>{episodeSaving ? 'Saving...' : 'Saved'}</span>    
                            </div> */}
                        </div>
                        
                        <div className='flex items-center gap-2'>
                            <div className='flex items-center overflow-hidden rounded-lg'>
                                <Link to={`/show/episodes/${episodeId}/script`} className={`cursor-pointer text-xs gap-3 flex items-center py-2 px-3 border-r ${pathname === `/show/episodes/${episodeId}/script` ? 'text-black bg-primary pointer-events-none' : 'bg-secondary/60 text-secondary-foreground/80 hover:bg-secondary hover:text-secondary-foreground'}`}>
                                    <LuLetterText className='w-4 h-4' />
                                    Script
                                </Link>
                                <Link to={`/show/episodes/${episodeId}/${segmentId || episode?.segments?.[0]?._id}/storyboard`} className={`cursor-pointer text-xs gap-3 flex items-center py-2 px-3 ${isModeActive(`storyboard`)}`}>
                                    <CiViewTimeline className='w-4 h-4' />
                                    Storyboard
                                </Link>
                            </div>   
                            <UserRole role='editor'>
                                <DropdownMenu>
                                    <DropdownMenuTrigger>
                                        <div className='flex items-center gap-2 bg-secondary text-secondary-foreground border shadow hover:bg-secondary/80 p-2 text-xs rounded-lg'>
                                            <FiMoreHorizontal className='w-4 h-4' />
                                        </div>
                                        
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        <DropdownMenuItem onClick={() => handleExport('doc')}>
                                            <TbFileText className='w-4 h-4' />
                                            Export Script
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={handleExportStoryboardAssets} disabled={exporting || !segmentId}>
                                            <TbFileTypeZip className='w-4 h-4' />
                                            Export Storyboard
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </UserRole>
                        </div>
                    </div>
                    {/* <div className='flex flex-col gap-2 pl-10'>
                        
                        <div className='flex items-center gap-6'>
                            <div className='flex items-center gap-2 flex-wrap'>
                                <span className='text-xs text-secondary-foreground/80'>Word count:</span>
                                <span className='text-xs text-secondary-foreground'>{episode.word_count}</span>
                            </div>
                            <div className='flex items-center gap-2 flex-wrap'>
                                <span className='text-xs text-secondary-foreground/80'>Audio duration:</span>
                                <span className='text-xs text-secondary-foreground'>{dayjs.duration(episode.audio_duration, 'milliseconds').format('HH:mm:ss')}</span>
                            </div>
                            <div className='flex items-center gap-2 flex-wrap'>
                                <span className='text-xs text-secondary-foreground/80'>Assets:</span>
                                <span className='text-xs text-secondary-foreground'>{episode.total_assets}</span>
                            </div>
                        </div>
                    </div> */}
                </div>
            
            
            <div className="flex flex-1 overflow-hidden">
                <div className={`flex flex-col gap-7 border-r border-r-secondary/60 py-4 px-2 group/segments ${segmentsPanelCollapsed ? 'w-14 min-w-14' : 'w-full max-w-64'}`}>
                    <div className={`flex w-full justify-between items-center flex-none ${segmentsPanelCollapsed ? 'justify-center' : 'justify-between'}`}>
                        {!segmentsPanelCollapsed && <h1 className="text-xl font-bold text-secondary-foreground flex-1 border-l-2 border-l-primary pl-4">Segments</h1>}
                        <button onClick={() => setSegmentsPanelCollapsed(!segmentsPanelCollapsed)} className='text-secondary-foreground/60 hover:text-secondary-foreground p-1 rounded-md hover:bg-secondary/60 transition-colors focus:outline-none focus:ring-0'>
                            {segmentsPanelCollapsed ? <FiChevronsRight className='w-4 h-4' /> : <FiChevronsLeft className='w-4 h-4' />}
                        </button>
                    </div>
                    <div className={`flex flex-col gap-4 justify-start flex-1 ${segmentsPanelCollapsed ? 'items-center' : ''}`}>
                        {episode.segments?.map((s, index) =>
                            <div key={s._id} className='flex items-center gap-2 justify-between group/segment-name'>
                                <Link key={s._id} to={`/show/episodes/${episodeId}/${s._id}/${mode}`} className={`cursor-pointer text-sm flex flex-col ${isSegmentActive(`/show/episodes/${episodeId}/${s._id}/${mode}`)}`}>
                                    {segmentsPanelCollapsed ? (
                                        <span className='w-8 h-8 flex items-center justify-center rounded-md bg-secondary/60 text-xs font-bold cursor-pointer'>S{index + 1}</span>
                                    ) : (
                                        <>
                                            {s.name}
                                            {s.instructions && <span className='text-[10px] text-secondary-foreground/60'>{show.segments.find(i => i._id === s.instructions)?.name}</span>}
                                        </>
                                    )}
                                </Link>
                                {!segmentsPanelCollapsed && (
                                    <div className='flex items-center gap-2 opacity-0 group-hover/segment-name:opacity-100'>
                                        <UserRole role='editor'>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger>
                                                    <div className='flex items-center bg-secondary text-secondary-foreground hover:bg-secondary/80 p-1 text-xs rounded-md'>
                                                        <FiMoreHorizontal className='w-3 h-3' />
                                                    </div>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent>
                                                    <DropdownMenuItem className='p-1 text-xs' disabled={index === 0} onClick={() => handleReorderSegment(s, index - 1)}>
                                                        <FiChevronUp className='w-4 h-4' />
                                                        Move Up
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem className='p-1 text-xs' disabled={index === episode.segments.length - 1} onClick={() => handleReorderSegment(s, index + 1)}>
                                                        <FiChevronDown className='w-4 h-4' />
                                                        Move Down
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem className='p-1 text-xs' onClick={() => handleNewChat(s)}>
                                                        <LuBrainCircuit className='w-4 h-4' />
                                                        Angent
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem className='p-1 text-xs' onClick={() => handleCreateTimeline(s)}>
                                                        <RiFilmLine className='w-4 h-4' />
                                                        Timeline
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem className='p-1 text-xs' onClick={() => navigator.clipboard.writeText(s._id)}>
                                                        <FiCopy className='w-4 h-4' />
                                                        Copy ID
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem className='p-1 text-xs' onClick={()=>handleDeleteSegment(s)}>
                                                        <FiTrash2 className='w-4 h-4 text-red-500' />
                                                        Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </UserRole>
                                    </div>
                                )}
                            </div>
                        )}
                        {!segmentsPanelCollapsed && (
                            <UserRole role='editor'>
                                <Button type='secondary' size='xs' icon={<FiPlus className='w-3 h-3' />} onClick={handleAddSegment} className='hidden group-hover/segments:flex' >Add Segment</Button>
                            </UserRole>
                        )}
                    </div>
                </div>    
                <div className="flex flex-col flex-1 overflow-hidden relative w-full">
                    <Outlet />
                </div>
            </div>
            {updating && <Loader status={loadingStatus} />}
        </div>
    )
}
